/**
 * Multi-Pack Inventory Manager
 *
 * This module handles dynamic updates for multi-pack variants (2-pack, 4-pack)
 * based on the 1-pack variant's inventory after AJAX variant changes.
 *
 * The metafield `custom.single_pack_variant_id` is stored at the VARIANT level:
 * - 1-pack variant: No metafield (it's the base unit)
 * - 2-pack variant: Has metafield pointing to 1-pack variant ID
 * - 4-pack variant: Has metafield pointing to 1-pack variant ID
 *
 * The initial page load state is handled by Liquid templates:
 * - product-variant-options.liquid calculates virtual availability on render
 * - buy-buttons.liquid sets correct button state on render
 *
 * This module handles:
 * - Dynamic variant changes (after renderProductInfo re-fetches HTML)
 * - Synchronizing button state via MutationObserver
 * - Updating quantity max values
 */
(function() {
  'use strict';

  // Configuration
  const DEBUG = false; // Set to true for console logging
  const PACK_PATTERN = /(\d+)\s*Pack/i;

  /**
   * Log debug messages when DEBUG is enabled
   */
  function log(...args) {
    if (DEBUG) {
      console.log('[MultiPackInventory]', ...args);
    }
  }

  /**
   * Extract pack count from a variant title or option value
   * @param {string} text - The text to parse (e.g., "2 Pack", "4 Pack")
   * @returns {number|null} - The pack count or null if not found
   */
  function extractPackCount(text) {
    if (!text) return null;
    const match = text.match(PACK_PATTERN);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Check if a variant is a multi-pack (has singlePackVariantId)
   * @param {Object} variant - The variant object
   * @returns {boolean} - True if this is a multi-pack variant
   */
  function isMultiPackVariant(variant) {
    return variant.singlePackVariantId !== null && variant.singlePackVariantId !== undefined;
  }

  /**
   * Check if a variant should bypass inventory checks
   * (e.g., "continue selling when out of stock" is enabled)
   * @param {Object} variant - The variant object
   * @returns {boolean} - True if inventory checks should be bypassed
   */
  function shouldBypassInventoryCheck(variant) {
    return variant.inventory_management !== 'shopify' || variant.inventory_policy === 'continue';
  }

  /**
   * Get the 1-pack variant for a given variant
   * @param {Object} variant - The variant to check
   * @param {Array} allVariants - All variants array
   * @returns {Object|null} - The 1-pack variant object or null
   */
  function getOnePackVariant(variant, allVariants) {
    if (!isMultiPackVariant(variant)) {
      // This is the 1-pack itself
      return variant;
    }
    // This is a multi-pack, find the referenced 1-pack variant
    return allVariants.find(v => v.id === variant.singlePackVariantId) || null;
  }

  /**
   * Get the 1-pack inventory for a given variant
   * @param {Object} variant - The variant to check
   * @param {Array} allVariants - All variants array
   * @returns {number} - The 1-pack inventory quantity
   */
  function getOnePackInventory(variant, allVariants) {
    const onePack = getOnePackVariant(variant, allVariants);
    return onePack ? (onePack.inventory_quantity || 0) : 0;
  }

  /**
   * Find the 1-pack variant from the variants array
   * @param {Array} variants - Array of all variants
   * @returns {Object|null} - The 1-pack variant object or null
   */
  function findOnePackVariant(variants) {
    // The 1-pack variant is the one WITHOUT a singlePackVariantId metafield
    // Or we can find it by title
    const onePackByMeta = variants.find(v => !isMultiPackVariant(v));
    if (onePackByMeta) return onePackByMeta;

    // Fallback: find by title
    return variants.find(v => {
      const packCount = extractPackCount(v.title);
      return packCount === 1;
    }) || null;
  }

  /**
   * Calculate if a variant is available based on 1-pack inventory
   * @param {Object} variant - The variant to check
   * @param {Array} allVariants - All variants array
   * @returns {boolean} - True if variant should be available
   */
  function calculateVariantAvailability(variant, allVariants) {
    const packCount = extractPackCount(variant.title);
    if (packCount === null) return variant.available;

    // Get the 1-pack variant to check its inventory policy
    const onePack = getOnePackVariant(variant, allVariants);

    // If the 1-pack variant has "continue selling when out of stock" enabled
    // or is not tracked by Shopify, all pack sizes should be available
    if (onePack && shouldBypassInventoryCheck(onePack)) {
      log(`Pack ${packCount}: Bypassing inventory check (policy: ${onePack.inventory_policy}, management: ${onePack.inventory_management})`);
      return true;
    }

    const onePackInventory = getOnePackInventory(variant, allVariants);

    if (packCount === 1) {
      return onePackInventory > 0;
    }

    return onePackInventory >= packCount;
  }

  /**
   * Update variant option input states based on calculated availability
   * @param {HTMLElement} variantSelects - The variant-selects element
   * @param {Array} variants - All variants with their metafield data
   */
  /**
   * Get currently selected options from the variant picker
   * @returns {Object} - Map of option names to selected values
   */
  function getSelectedOptions() {
    const selected = {};
    // Get selected radio buttons
    document.querySelectorAll('variant-selects input[type="radio"]:checked, variant-radios input[type="radio"]:checked').forEach(el => {
      const fieldset = el.closest('fieldset');
      const legend = fieldset?.querySelector('legend');
      if (legend) {
        const name = legend.textContent.trim();
        selected[name] = el.value;
      }
    });
    // Get selected dropdown values
    document.querySelectorAll('variant-selects select').forEach(el => {
      const fieldset = el.closest('fieldset');
      const legend = fieldset?.querySelector('legend');
      if (legend && el.value) {
        const name = legend.textContent.trim();
        selected[name] = el.value;
      }
    });
    return selected;
  }

  /**
   * Find variant matching given option value and currently selected other options
   * @param {string} value - The option value to match (e.g., "2 Pack")
   * @param {Array} variants - All variants array
   * @returns {Object|null} - Matching variant or null
   */
  function findMatchingVariant(value, variants) {
    const valueLower = value.toLowerCase();

    // First try exact title match (for single-option products)
    let matchingVariant = variants.find(v =>
      v.title.toLowerCase() === valueLower || v.title === value
    );

    if (matchingVariant) return matchingVariant;

    // For multi-option products, check if value is in the options array
    // and match against currently selected options for other option groups
    const selectedOptions = getSelectedOptions();

    matchingVariant = variants.find(v => {
      if (!v.options || !Array.isArray(v.options)) return false;

      // Check if this variant has the target option value
      const hasTargetOption = v.options.some(opt =>
        opt.toLowerCase() === valueLower || opt === value
      );
      if (!hasTargetOption) return false;

      // For Pack Size options, we want to find a variant that:
      // 1. Contains the pack size we're checking
      // 2. Matches the currently selected flavor (if any)
      // Since all pack sizes for a flavor share the same 1-pack inventory,
      // any matching variant will work for availability calculation
      return true;
    });

    return matchingVariant || null;
  }

  function updateVariantInputStates(variantSelects, variants) {
    if (!variantSelects) return;

    // Find all variant inputs (radio buttons for pill/button type)
    const inputs = variantSelects.querySelectorAll('input[type="radio"]');

    inputs.forEach(input => {
      const value = input.value;

      // Find the matching variant using improved matching
      const matchingVariant = findMatchingVariant(value, variants);

      if (!matchingVariant) {
        log(`No matching variant found for: ${value}`);
        return;
      }

      const isAvailable = calculateVariantAvailability(matchingVariant, variants);
      const packCount = extractPackCount(matchingVariant.title);
      const onePackInventory = getOnePackInventory(matchingVariant, variants);

      log(`Pack ${packCount}: 1-pack inventory=${onePackInventory}, available=${isAvailable}`);

      // Toggle the 'disabled' class (this triggers the crossed-out styling)
      // DO NOT disable the input - allow selection of sold-out variants
      // so users can see "Sold Out" state and access "Notify Me" button
      input.classList.toggle('disabled', !isAvailable);
    });

    // Also update dropdown options if they exist
    // DO NOT disable options - allow selection of sold-out variants
    const options = variantSelects.querySelectorAll('option');
    options.forEach(option => {
      const value = option.value;

      // Find the matching variant using improved matching
      const matchingVariant = findMatchingVariant(value, variants);

      if (!matchingVariant) return;

      const isAvailable = calculateVariantAvailability(matchingVariant, variants);

      // Update text to show sold out status, but don't disable the option
      if (!isAvailable) {
        const unavailableText = window.variantStrings?.unavailable_with_option || '(Sold out)';
        if (!option.textContent.includes(unavailableText)) {
          option.textContent = `${value} ${unavailableText}`;
        }
      } else {
        option.textContent = value;
      }
    });
  }

  /**
   * Update the Add to Cart button and Notify Me button states
   * @param {boolean} isAvailable - Whether the current variant is available
   * @param {string} sectionId - The section ID
   */
  function updateButtonStates(isAvailable, sectionId) {
    // Find the product form
    const productForm = document.getElementById(`product-form-${sectionId}`);
    if (!productForm) {
      log('Product form not found:', `product-form-${sectionId}`);
      return;
    }

    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span:first-child');

    if (!addButton) {
      log('Add button not found');
      return;
    }

    // Find the Notify Me button
    const notifyMeBtn = document.getElementById('notify-me-btn');

    // Update the notify-me-btn's variant-id data attribute to match the current variant
    const currentVariantInput = document.querySelector('input.product-variant-id[name="id"]');
    if (notifyMeBtn && currentVariantInput && currentVariantInput.value) {
      notifyMeBtn.dataset.variantId = currentVariantInput.value;
    }

    if (!isAvailable) {
      // Variant is not available
      addButton.setAttribute('disabled', 'disabled');
      addButton.classList.add('product_sold-out');

      // Store the unavailable state on the button for persistence
      addButton.dataset.multiPackUnavailable = 'true';

      if (addButtonText) {
        addButtonText.textContent = window.variantStrings?.soldOut || 'Sold out';
      }

      // Show Notify Me button (custom, may be hidden by default)
      if (notifyMeBtn) {
        notifyMeBtn.classList.remove('hidden');
      }

      log('Variant unavailable - showing Notify Me button');
    } else {
      // Variant is available
      addButton.removeAttribute('disabled');
      addButton.classList.remove('product_sold-out');

      // Remove the unavailable state marker
      delete addButton.dataset.multiPackUnavailable;

      if (addButtonText) {
        addButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      }

      // Hide Notify Me button (custom)
      if (notifyMeBtn) {
        notifyMeBtn.classList.add('hidden');
      }

      log('Variant available - hiding Notify Me button');
    }

    // Sync sticky bar button state with main button
    if (typeof window.syncStickyButtonWithMain === 'function') {
      window.syncStickyButtonWithMain();
    }
  }

  /**
   * Setup a persistent observer to watch the Add to Cart button
   * and re-apply disabled state if it gets incorrectly enabled by renderProductInfo
   * @param {string} sectionId - The section ID
   */
  function setupButtonObserver(sectionId) {
    const productForm = document.getElementById(`product-form-${sectionId}`);
    if (!productForm) return null;

    const addButton = productForm.querySelector('[name="add"]');
    if (!addButton) return null;

    // Create an observer to watch for attribute changes on the button
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          // Check if the button should be disabled based on our multi-pack logic
          if (addButton.dataset.multiPackUnavailable === 'true' && !addButton.hasAttribute('disabled')) {
            log('Button was re-enabled by external code - re-disabling');
            addButton.setAttribute('disabled', 'disabled');
            addButton.classList.add('product_sold-out');

            // Also show the Notify Me button
            const notifyMeBtn = document.getElementById('notify-me-btn');
            if (notifyMeBtn) {
              notifyMeBtn.classList.remove('hidden');
            }

            // Sync sticky bar button state
            if (typeof window.syncStickyButtonWithMain === 'function') {
              window.syncStickyButtonWithMain();
            }
          }
        }
      });
    });

    observer.observe(addButton, {
      attributes: true,
      attributeFilter: ['disabled']
    });

    log('Button observer setup complete');

    return observer;
  }

  /**
   * Calculate maximum quantity that can be ordered for a pack variant
   * @param {Object} variant - The variant
   * @param {Array} allVariants - All variants
   * @returns {number} - Maximum quantity
   */
  function calculateMaxQuantity(variant, allVariants) {
    const packCount = extractPackCount(variant.title);
    if (packCount === null || packCount === 0) return 99;

    const onePackInventory = getOnePackInventory(variant, allVariants);
    return Math.floor(onePackInventory / packCount);
  }

  /**
   * Update quantity input max value based on pack size
   * @param {string} sectionId - The section ID
   * @param {Object} variant - The current variant
   * @param {Array} allVariants - All variants
   */
  function updateQuantityMax(sectionId, variant, allVariants) {
    const quantityInput = document.querySelector(`#Quantity-${sectionId}`);
    if (!quantityInput) return;

    const maxQty = calculateMaxQuantity(variant, allVariants);
    const packCount = extractPackCount(variant.title);

    if (maxQty > 0) {
      quantityInput.max = maxQty;
      quantityInput.dataset.max = maxQty;

      // If current value exceeds max, reset to max
      if (parseInt(quantityInput.value, 10) > maxQty) {
        quantityInput.value = maxQty;
      }
    }

    log(`Updated max quantity to ${maxQty} for ${packCount}-pack`);
  }

  /**
   * Handle variant change and update UI accordingly
   * @param {Object} newVariant - The newly selected variant (from Shopify's event)
   * @param {Array} variants - All variants with metafield data
   * @param {string} sectionId - The section ID
   * @param {HTMLElement} variantSelects - The variant-selects element
   */
  function handleVariantChange(newVariant, variants, sectionId, variantSelects) {
    if (!newVariant) return;

    log('Handling variant change:', newVariant.title);

    // Find the variant in our data (which has the singlePackVariantId)
    const variantWithMeta = variants.find(v => v.id === newVariant.id);
    const variantToCheck = variantWithMeta || newVariant;

    const isAvailable = calculateVariantAvailability(variantToCheck, variants);

    // Update variant input states
    updateVariantInputStates(variantSelects, variants);

    // Update button states immediately
    updateButtonStates(isAvailable, sectionId);

    // Also update after a delay to catch renderProductInfo completion
    setTimeout(() => {
      updateButtonStates(isAvailable, sectionId);
    }, 500);

    // Update quantity max
    updateQuantityMax(sectionId, variantToCheck, variants);
  }

  /**
   * Main initialization function
   */
  function init() {
    // Check if we have the multi-pack data injected by Liquid
    const multiPackDataEl = document.getElementById('multi-pack-inventory-data');
    if (!multiPackDataEl) {
      log('No multi-pack inventory data found - module inactive');
      return;
    }

    let config;
    try {
      config = JSON.parse(multiPackDataEl.textContent);
    } catch (e) {
      console.error('[MultiPackInventory] Failed to parse config:', e);
      return;
    }

    const { sectionId, variants } = config;

    // Check if any variant has the singlePackVariantId (meaning multi-pack is enabled)
    const hasAnyMultiPack = variants.some(v => isMultiPackVariant(v));
    if (!hasAnyMultiPack) {
      log('No variants with singlePackVariantId found - module inactive');
      return;
    }

    log('Module activated with config:', config);
    log('Variants with metafield:', variants.filter(v => isMultiPackVariant(v)).map(v => v.title));
    window._multiPackInventoryActive = true;

    // Find the 1-pack variant for logging
    const onePackVariant = findOnePackVariant(variants);
    if (onePackVariant) {
      log('1-pack variant found:', onePackVariant.title, 'inventory:', onePackVariant.inventory_quantity);
    }

    // Find the variant-selects element
    const variantSelects = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
    if (!variantSelects) {
      log('variant-selects element not found');
      return;
    }

    // Setup button observer to catch when renderProductInfo re-enables the button
    setupButtonObserver(sectionId);

    // Subscribe to variant change events (Shopify's pub/sub system)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== sectionId) return;

        const newVariant = event.data.variant;
        log('variantChange event received:', newVariant?.title);

        // Try to get updated variant data from the event's HTML
        let updatedVariants = variants;
        if (event.data.html) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = event.data.html.innerHTML || event.data.html;
          const updatedDataEl = tempDiv.querySelector('#multi-pack-inventory-data');
          if (updatedDataEl) {
            try {
              const updatedConfig = JSON.parse(updatedDataEl.textContent);
              if (updatedConfig.variants) {
                updatedVariants = updatedConfig.variants;
                log('Updated variants from event');
              }
            } catch (e) {
              log('Failed to parse updated config');
            }
          }
        }

        handleVariantChange(newVariant, updatedVariants, sectionId, variantSelects);
      });

      log('Subscribed to variantChange events');
    }

    // Also listen for direct change events on the variant-selects element
    variantSelects.addEventListener('change', () => {
      const variantsScript = variantSelects.querySelector('script[type="application/json"]:not([data-selected-variant])');
      if (!variantsScript) return;

      try {
        const allVariants = JSON.parse(variantsScript.textContent);
        const selectedInputs = variantSelects.querySelectorAll('input[type="radio"]:checked');
        const selectedOptions = Array.from(selectedInputs).map(el => el.value);

        // Handle single option case
        if (selectedOptions.length === 0) {
          const selectedOption = variantSelects.querySelector('select');
          if (selectedOption) {
            selectedOptions.push(selectedOption.value);
          }
        }

        const matchingVariant = allVariants.find(v =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (matchingVariant) {
          // Merge with our variant data that has singlePackVariantId
          const variantWithMeta = variants.find(v => v.id === matchingVariant.id);
          const mergedVariant = { ...matchingVariant, ...variantWithMeta };
          handleVariantChange(mergedVariant, variants, sectionId, variantSelects);
        }
      } catch (e) {
        log('Failed to find matching variant:', e);
      }
    });

    log('Initialization complete');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Small delay to ensure variant-selects custom element is defined
    setTimeout(init, 50);
  }
})();
